import { Avatar, Card, CardContent, Container, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useEffect, useState } from 'react';
import GridCellExpand from '../../components/DataGridTooltips.jsx';
import dataGridStyles from '../../components/styles/dataGridStyles.js';
import getBaseURL from '../../utils/getBaseURL.js';
import '../../components/styles/iframe.css';

// Specific API for iframe (without authentication interceptors)
const iframeApi = axios.create({
    baseURL: getBaseURL(),
});

// columns simplified for the iframe (without authentication-dependent features)
const iframeEmployeesColumns = [
    {
        field: 'photo_url',
        headerName: '',
        width: 60,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <div style={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
                    <Avatar src={params.value} sx={{ width: 30, height: 30 }} />
                </div>
            );
        }
    },
    {
        field: 'full_name',
        headerName: 'Name',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''} >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    },
    {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''} >
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    },
    {
        field: 'department',
        headerName: 'Department',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''} >
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    },
    {
        field: 'coach',
        headerName: 'Coach',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''} >
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    },
    {
        field: 'work_location',
        headerName: 'Work Location',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''}>
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    },
    {
        field: 'phone',
        headerName: 'Phone',
        flex: 1,
        headerClassName: 'header-class',
        renderCell: (params) => {
            return (
                <GridCellExpand value={params.value || ''} >
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {params.value}
                    </div>
                </GridCellExpand>
            );
        }
    }
];

const EmployeesIframe = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // add class for iframe-specific styles
        document.body.classList.add('iframe-mode');

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('iframe-mode');
        };
    }, []);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                // using the iframe-specific API without authentication
                const response = await iframeApi.get('/users/public');
                const usersFiltered = response.data
                    ?.filter(user => user.etro_team && !user.suspended)
                    ?.sort((a, b) => a.full_name.localeCompare(b.full_name));

                const usersFixed = usersFiltered?.map(user => ({
                    id: user?.id,
                    photo_url: user?.photo_url,
                    full_name: user?.full_name,
                    title: user?.title?.title,
                    department: user?.department?.department,
                    coach: user?.coach,
                    work_location: user?.work_location?.building_name,
                    phone: user?.phone
                }));
                setRows(usersFixed || []);
            } catch (err) {
                console.error('Error fetching employees for iframe:', err);
                setError('Error on fetching employees');
            } finally {
                setLoading(false);
            }
        };
        fetchEmployees();
    }, []); // Remove dependência de employees

    return (
        <div className="iframe-container">
            <Container
                maxWidth={false}
                sx={{
                    py: 1,
                    px: 1,
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {error ? (
                    <Typography color="error" align="center">
                        {error}
                    </Typography>
                ) : (
                    <Card sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100vh - 60px)'
                    }}>
                        <CardContent sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '8px !important'
                        }}>
                            <DataGrid
                                rows={rows}
                                columns={iframeEmployeesColumns}
                                // loading={loading}
                                localeText={{
                                    noRowsLabel: 'No rows 123',
                                }}
                                slotProps={{
                                    loadingOverlay: {
                                        variant: 'skeleton',
                                        noRowsVariant: 'skeleton'
                                    }
                                }}
                                sx={{
                                    ...dataGridStyles,
                                    flexGrow: 1,
                                    border: 'none',
                                    '& .MuiDataGrid-main': {
                                        overflow: 'auto'
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 50,
                                        },
                                    },
                                }}
                                pageSizeOptions={[25, 50, 100]}
                                disableRowSelectionOnClick
                                disableColumnMenu
                                density="standard"
                                hideFooterSelectedRowCount
                            />
                        </CardContent>
                    </Card>
                )}
            </Container>
        </div>
    );
};

export default EmployeesIframe;
